import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import {
  Users, MagnifyingGlass, Eye, Fire, ChartBar,
  ClockCounterClockwise, ArrowsClockwise, ShieldCheck, ArrowLeft,
} from "@phosphor-icons/react";

import Header from "@/components/Header";
import VerdictBadge from "@/components/VerdictBadge";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VERDICT_ACCENT = {
  TRUE: "bg-emerald-500 text-white",
  FALSE: "bg-red-500 text-white",
  PARTIALLY_TRUE: "bg-amber-400 text-black",
  OUTDATED: "bg-orange-400 text-black",
  UNVERIFIABLE: "bg-zinc-400 text-white",
};

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_admin) return;
    fetchStats();
  }, [authLoading, user]);

  if (authLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center text-zinc-900">
        <div className="font-mono-plex text-[10px] font-semibold text-zinc-500">CHECKING PERMISSIONS…</div>
      </div>
    );
  }

  if (!user || !user.is_admin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen w-full flex flex-col text-zinc-900">
      <Header />
      <section
        data-testid="admin-dashboard"
        className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pt-8 pb-16"
      >
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 font-mono-plex text-[10px] font-semibold text-zinc-700 hover:text-zinc-950 mb-4"
        >
          <ArrowLeft weight="bold" className="w-3.5 h-3.5" />
          BACK TO TRUESTREAM
        </Link>
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="font-mono-plex text-[10px] font-semibold text-emerald-700 mb-2 flex items-center gap-2">
              <ShieldCheck weight="fill" className="w-3.5 h-3.5" />
              ADMIN · {user.email}
            </div>
            <h1 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight text-zinc-950">
              Command deck.
            </h1>
          </div>
          <button
            onClick={fetchStats}
            disabled={loading}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 border border-zinc-900 bg-white text-zinc-950 px-3 py-2 font-mono-plex text-[10px] font-semibold hover:bg-black hover:text-white disabled:opacity-50 transition-colors"
          >
            <ArrowsClockwise weight="bold" className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            REFRESH
          </button>
        </div>

        {error && (
          <div className="border-2 border-red-500 bg-red-50 text-red-900 px-4 py-3 mb-6 text-sm">
            {error}
          </div>
        )}

        {loading && !stats ? (
          <div className="font-mono-plex text-[10px] font-semibold text-zinc-500">LOADING METRICS…</div>
        ) : stats ? (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <Kpi
                icon={Users}
                label="Signed-up users"
                value={stats.users.total}
                sublabel={`${stats.users.last_7d} in the last 7d · ${stats.users.last_24h} in 24h`}
                testId="kpi-users"
              />
              <Kpi
                icon={MagnifyingGlass}
                label="Fact-checks run"
                value={stats.fact_checks.total}
                sublabel={`${stats.fact_checks.last_7d} in the last 7d · ${stats.fact_checks.last_24h} in 24h`}
                testId="kpi-checks"
              />
              <Kpi
                icon={Eye}
                label="Verdict views"
                value={stats.fact_checks.total_views}
                sublabel="Across all cached verdicts"
                testId="kpi-views"
              />
              <Kpi
                icon={Fire}
                label="Active sessions"
                value={stats.users.active_sessions}
                sublabel="Logged-in right now"
                testId="kpi-sessions"
              />
            </div>

            {/* Two-column breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
              {/* Verdict distribution */}
              <div className="border border-zinc-300 bg-white p-5">
                <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 mb-3 flex items-center gap-2">
                  <ChartBar weight="fill" className="w-3.5 h-3.5" />
                  VERDICT DISTRIBUTION
                </div>
                {Object.keys(stats.verdicts).length === 0 ? (
                  <div className="text-zinc-500 text-sm">No verdicts yet.</div>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(stats.verdicts)
                      .sort((a, b) => b[1] - a[1])
                      .map(([verdict, count]) => {
                        const total = Object.values(stats.verdicts).reduce((a, b) => a + b, 0);
                        const pct = total ? Math.round((count / total) * 100) : 0;
                        return (
                          <div key={verdict} className="flex items-center gap-3" data-testid={`verdict-row-${verdict}`}>
                            <VerdictBadge verdict={verdict} size="sm" />
                            <div className="flex-1 h-3 bg-zinc-100 relative overflow-hidden">
                              <div
                                className={`h-full ${VERDICT_ACCENT[verdict] || "bg-zinc-500"}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <div className="font-mono-plex text-[11px] font-semibold text-zinc-700 w-16 text-right">
                              {count} · {pct}%
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>

              {/* Platform breakdown */}
              <div className="border border-zinc-300 bg-white p-5">
                <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 mb-3">
                  PLATFORM BREAKDOWN
                </div>
                {Object.keys(stats.platforms).length === 0 ? (
                  <div className="text-zinc-500 text-sm">No fact-checks yet.</div>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(stats.platforms)
                      .sort((a, b) => b[1] - a[1])
                      .map(([platform, count]) => {
                        const total = Object.values(stats.platforms).reduce((a, b) => a + b, 0);
                        const pct = total ? Math.round((count / total) * 100) : 0;
                        return (
                          <div key={platform} className="flex items-center gap-3">
                            <span className="font-mono-plex text-[10px] font-semibold text-zinc-700 uppercase w-24">
                              {platform || "unknown"}
                            </span>
                            <div className="flex-1 h-3 bg-zinc-100">
                              <div className="h-full bg-black" style={{ width: `${pct}%` }} />
                            </div>
                            <div className="font-mono-plex text-[11px] font-semibold text-zinc-700 w-16 text-right">
                              {count} · {pct}%
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>

            {/* Top + Recent */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <Panel title="TOP FACT-CHECKS BY VIEWS" icon={Fire} testId="top-checks-panel">
                {stats.top_checks.length === 0 ? (
                  <EmptyRow>No fact-checks yet.</EmptyRow>
                ) : (
                  stats.top_checks.map((c) => <CheckRow key={c.cache_key} check={c} />)
                )}
              </Panel>
              <Panel title="MOST RECENT FACT-CHECKS" icon={ClockCounterClockwise} testId="recent-checks-panel">
                {stats.recent_checks.length === 0 ? (
                  <EmptyRow>No fact-checks yet.</EmptyRow>
                ) : (
                  stats.recent_checks.map((c) => <CheckRow key={c.cache_key} check={c} />)
                )}
              </Panel>
            </div>

            {/* Recent users */}
            <div className="mt-6">
              <Panel title="RECENT SIGN-UPS" icon={Users} testId="recent-users-panel">
                {stats.recent_users.length === 0 ? (
                  <EmptyRow>No sign-ups yet.</EmptyRow>
                ) : (
                  <div className="divide-y divide-zinc-200">
                    {stats.recent_users.map((u) => (
                      <div key={u.user_id} className="py-2.5 flex items-center gap-3">
                        {u.picture ? (
                          <img src={u.picture} alt="" referrerPolicy="no-referrer" className="w-7 h-7 rounded-full" />
                        ) : (
                          <span className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px] font-bold">
                            {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                          </span>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-mono-plex text-[12px] font-semibold text-zinc-900 normal-case tracking-normal truncate">
                            {u.name || "—"}
                          </div>
                          <div className="text-[11px] text-zinc-500 truncate">{u.email}</div>
                        </div>
                        <div className="font-mono-plex text-[10px] text-zinc-500 flex-shrink-0">
                          {formatDate(u.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            </div>

            <div className="mt-6 font-mono-plex text-[9px] text-zinc-400">
              GENERATED {stats.generated_at ? new Date(stats.generated_at).toLocaleString() : ""}
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sublabel, testId }) {
  return (
    <div
      data-testid={testId}
      className="border-2 border-black bg-white p-4 shadow-[4px_4px_0px_rgba(0,0,0,1)]"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono-plex text-[10px] font-semibold text-zinc-500">{label.toUpperCase()}</span>
        <Icon weight="duotone" className="w-4 h-4 text-zinc-900" />
      </div>
      <div className="font-display text-4xl text-zinc-950 leading-none mb-1">{formatNumber(value)}</div>
      {sublabel && <div className="font-mono-plex text-[9px] text-zinc-500">{sublabel.toUpperCase()}</div>}
    </div>
  );
}

function Panel({ title, icon: Icon, children, testId }) {
  return (
    <div data-testid={testId} className="border border-zinc-300 bg-white p-5">
      <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 mb-3 flex items-center gap-2">
        {Icon && <Icon weight="fill" className="w-3.5 h-3.5" />}
        {title}
      </div>
      {children}
    </div>
  );
}

function CheckRow({ check }) {
  return (
    <a
      href={`/check/${check.cache_key}`}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-3 py-2 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50"
    >
      <VerdictBadge verdict={check.overall_verdict} size="sm" />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-zinc-900 truncate">{check.url}</div>
        <div className="font-mono-plex text-[9px] text-zinc-500 flex items-center gap-2">
          <span>{check.platform || "unknown"}</span>
          <span className="text-zinc-300">·</span>
          <span>{Math.round((check.overall_confidence || 0) * 100)}%</span>
          <span className="text-zinc-300">·</span>
          <span>{check.view_count} views</span>
        </div>
      </div>
    </a>
  );
}

function EmptyRow({ children }) {
  return <div className="text-zinc-500 text-sm py-2">{children}</div>;
}

function formatNumber(n) {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat().format(n);
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}
